import * as React from 'react';
import { FieldArray, useFormik, FormikProvider } from 'formik';
import { get } from 'lodash';
import { Box, Button } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContentContext } from '@qbe/dfc-content';

import FormSpacer from 'components/FormSpacer/FormSpacer';
import { FormStepContentProps } from 'types';
import { IncomeFormFields, ExpenseFormFields, useFormState, IncomeData } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';

import IncomeStatus from './sections/IncomeStatus';
import IncomeEdit from './sections/IncomeEdit';
import IncomePreview from './sections/IncomePreview';
import Expense from './sections/Expense';
import validationSchema from './schema';
import { useContentComplaintPartners } from 'utils/content';
import { TrackEvent, formatNext } from 'hooks/useAnalytics';

type StepMethod = 'Edit' | 'Preview' | 'Expense';

type DivRefs = { [key: string]: React.RefObject<HTMLDivElement> };

interface IncomeExpenseFields extends IncomeFormFields, ExpenseFormFields {
  mode: StepMethod;
  enableAttachment: boolean;
  maxFileSize: number;
  allowedFileType: string[];
}

// Used formik for array component handling
const IncomeForm = ({ onComplete, showError, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData, updateForm } = useFormState();

  const { getTextContent } = useContentComplaintPartners({
    context: useContentContext(),
  });

  const [selectedIndex, setSelectedIndex] = React.useState<string>('');
  const [mode, setMode] = React.useState<StepMethod>('Edit');
  React.useEffect(() => {
    if (mode === 'Edit' || mode === 'Preview') {
      showError(false);
    }
  }, [mode, showError]);

  const initialValues: Partial<IncomeExpenseFields> = {
    ...formData,
    mode,
    enableAttachment: get(window, '_CMS_.ENABLE_FILE_UPLOAD', false),
    maxFileSize: get(window, '_CMS_.MAX_FILE_UPLOAD_SIZE_IN_MB', 10),
    allowedFileType: get(window, '_CMS_.ALLOWED_FILE_EXTENSIONS', []),
  };

  const [modalStatus, setModal] = React.useState<boolean>(false);

  const handleSubmit = (values: Partial<IncomeExpenseFields>) => {
    const _isEdit = mode === 'Edit';
    const _isPreview = mode === 'Preview';
    const _isExpense = mode === 'Expense';

    if (_isEdit) {
      const incomeDataValue = values.incomeStatus === 'no' ? [] : values.incomeData;
      updateForm({ incomeData: incomeDataValue, incomeStatus: values.incomeStatus });
      if (hasIncome) {
        setMode('Preview');
        setSelectedIndex('');
      } else {
        setMode('Expense');
        TrackEvent(formatNext('nextExpenseDetails'), values);
      }
    }

    if (_isPreview) {
      setMode('Expense');
      TrackEvent(formatNext('nextExpenseDetails'), values);
    }

    if (_isExpense && !modalStatus) {
      setModal(true);
    }
  };

  const formikBag = useFormik<Partial<IncomeExpenseFields>>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  React.useEffect(() => {
    formikBag.dirty && onStart();
  }, [formikBag.dirty, onStart]);

  const handleSubmitModal = () => {
    setModal(false);
    onComplete(formikBag.values);
  };
  const handleCloseModal = () => setModal(false);

  const isDisabled = !validationSchema.isValidSync(formikBag.values);
  const { incomeStatus, incomeData } = formikBag.values;
  const hasIncome = incomeStatus === 'yes';
  const withIncomeData = typeof incomeData !== 'undefined';

  const refs: DivRefs = withIncomeData
    ? incomeData.reduce((accumulator: DivRefs, value: IncomeData) => {
        accumulator[value.id] = React.createRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>;
        return accumulator;
      }, {})
    : {};

  const isEdit = mode === 'Edit';
  const isPreview = mode === 'Preview';
  const isExpense = mode === 'Expense';
  React.useEffect(() => {
    if (!isEdit) window.scrollTo(0, 0);

    if (isEdit && selectedIndex && refs[selectedIndex].current?.offsetTop) {
      const current = refs[selectedIndex].current;

      window.scrollTo(0, Number(refs[selectedIndex].current?.offsetTop));

      setTimeout(() => {
        window.scrollTo(0, Number(refs[selectedIndex].current?.offsetTop));
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, isEdit]);

  const handleEdit = (id: string) => {
    setMode('Edit');
    setSelectedIndex(id);
  };

  const showContinueBtnOutside = (isEdit && hasIncome) || isPreview;

  return (
    <FormikProvider value={formikBag}>
      <form onSubmit={formikBag.handleSubmit} noValidate>
        {isEdit && (
          <>
            <FormCard variant="pageTitle" title={getTextContent('Income.details.formHeading')} description="" />

            <FormSpacer type="pagesection" />

            <FieldArray name="incomeData">
              {(arrayHelpers) => (
                <>
                  <IncomeStatus arrayHelpers={arrayHelpers} />

                  {withIncomeData &&
                    incomeData.map((field, index) => (
                      <IncomeEdit key={field.id} ref={refs[field.id]} index={index} arrayHelpers={arrayHelpers} />
                    ))}
                </>
              )}
            </FieldArray>
          </>
        )}

        {isPreview && (
          <>
            <FormCard variant="pageTitle" title={getTextContent('Income.details.summary')} description="" />

            {withIncomeData &&
              incomeData.map((field) => (
                <IncomePreview key={field.id} field={field} onEdit={() => handleEdit(field.id)} />
              ))}
          </>
        )}

        {isExpense && (
          <Expense
            showModal={modalStatus}
            formik={formikBag}
            onSubmitModal={handleSubmitModal}
            onCloseModal={handleCloseModal}
          />
        )}

        {showContinueBtnOutside && (
          <>
            <FormSpacer type="pagesection" />
            <Box display="flex" justifyContent="center">
              <Button label={getTextContent('Income.details.submit.label')} type="submit" disabled={isDisabled} />
            </Box>
          </>
        )}
      </form>
    </FormikProvider>
  );
};

export default IncomeForm;
